import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { coreApi } from '../api/axios';
import { getDocumentStatusColor } from '../config/documentStatusColors';

const DocumentStatusesContext = createContext();

// Глобальные переменные для preload и Suspense
let statusesPromise = null;
let statusesCache = null;
const CACHE_TTL = 60 * 60 * 1000; // 1 час
const CACHE_KEY = 'document_statuses_cache_v2';
const CACHE_TIMESTAMP_KEY = 'document_statuses_cache_v2_timestamp';
const LEGACY_STATUS_CACHE_KEYS = [
  'document_statuses_cache',
  'document_statuses_cache_timestamp',
];

// Вспомогательные функции для работы с кешем
const getCachedStatuses = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < CACHE_TTL) {
        return JSON.parse(cached);
      }
    }
  } catch (err) {
    void err;
  }
  return null;
};

const setCachedStatuses = (statuses) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(statuses));
    sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (err) {
    void err;
  }
};

/**
 * Функция для preload статусов (вызывается при старте приложения)
 * Возвращает Promise для Suspense
 */
export const preloadDocumentStatuses = () => {
  // Проверяем кеш
  const cached = getCachedStatuses();
  if (cached) {
    statusesCache = cached;
    statusesPromise = Promise.resolve(cached);
    return statusesPromise;
  }
  
  // Если уже идет загрузка - возвращаем существующий Promise
  if (statusesPromise) {
    return statusesPromise;
  }
  
  // Создаем новый Promise для загрузки
  statusesPromise = coreApi.get('/operations/document-statuses')
    .then(response => {
      if (response.data && Array.isArray(response.data)) {
        const statuses = {};
        
        response.data.forEach(status => {
          if (status.code && status.name) {
            statuses[status.code] = {
              name: status.name,
              description: status.description || '',
              color: getDocumentStatusColor(status.code),
              code: status.code
            };
          }
        });
        
        statusesCache = statuses;
        setCachedStatuses(statuses);
        return statuses;
      }
      throw new Error('Invalid status data format');
    })
    .catch(err => {
      statusesPromise = null; // Сбрасываем для повторной попытки
      throw err;
    });
  
  return statusesPromise;
};

/**
 * Функция для инвалидации кеша (вызывается при logout или изменении данных)
 */
export const invalidateStatusesCache = () => {
  statusesCache = null;
  statusesPromise = null;
  sessionStorage.removeItem(CACHE_KEY);
  sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
  LEGACY_STATUS_CACHE_KEYS.forEach((key) => sessionStorage.removeItem(key));
};

/**
 * Provider для статусов документов
 */
export function DocumentStatusesProvider({ children }) {
  const [statusMap, setStatusMap] = useState(statusesCache || {});
  const [loading, setLoading] = useState(!statusesCache);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Если кеш уже есть - используем его
    if (statusesCache) {
      setStatusMap(statusesCache);
      setLoading(false);
      return () => {};
    }

    let cancelled = false;
    setLoading(true);
    preloadDocumentStatuses()
      .then(statuses => {
        if (!cancelled) {
          setStatusMap(statuses);
          setError(null);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);
  
  const getStatusName = useCallback((code) => {
    if (!code) return 'Неизвестно';
    return statusMap[code]?.name || code;
  }, [statusMap]);

  const getStatusColorByCode = useCallback((code) => {
    if (!code) return 'default';
    return statusMap[code]?.color || getDocumentStatusColor(code);
  }, [statusMap]);

  const value = useMemo(() => ({
    statusMap,
    loading,
    error,
    getStatusName,
    getStatusColorByCode
  }), [statusMap, loading, error, getStatusName, getStatusColorByCode]);

  return (
    <DocumentStatusesContext.Provider value={value}>
      {children}
    </DocumentStatusesContext.Provider>
  );
}

/**
 * Хук для доступа к статусам документов
 */
export function useDocumentStatuses() {
  const context = useContext(DocumentStatusesContext);
  if (!context) {
    throw new Error('useDocumentStatuses must be used within DocumentStatusesProvider');
  }
  return context;
}

/**
 * Хук с Suspense support (для будущего использования)
 * Бросает Promise для React Suspense если данные еще не загружены
 */
export function useDocumentStatusesSuspense() {
  // Если данных нет и идет загрузка - throw Promise для Suspense
  if (!statusesCache && statusesPromise) {
    throw statusesPromise;
  }
  
  // Если данных нет и загрузка не начата - запускаем
  if (!statusesCache && !statusesPromise) {
    throw preloadDocumentStatuses();
  }
  
  return useDocumentStatuses();
}
